import { useEffect, useState } from 'react';
import { Stack, Group, Button, Text, CopyButton } from '@repletoryreach/sdk-ui';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Checkbox } from '@repletoryreach/sdk-ui/inputs';
import { downloadText } from '@repletoryreach/sdk-utils/file';
import { useT } from '../../../i18n/I18nProvider';
import './one-time-secret.css';

interface Props {
  /** The secret itself, already in the form the operator should keep. */
  value: string | null;
  /** Rendered one per line when the secret is a set, such as recovery codes. */
  lines?: string[];
  title: string;
  /** What this value is and what it unlocks, in the operator's words. */
  intro: string;
  /** Suggested file name for the download, without a directory. */
  filename: string;
  onAcknowledge: () => void;
}

/**
 * A value the platform will never show again: a freshly minted API key, a set
 * of two-factor recovery codes.
 *
 * The console used to print these into a block of body text and let any stray
 * Escape key take them away for good, so an operator who blinked had to revoke
 * and start over. Three things make that impossible here: the dialog ignores
 * Escape and backdrop clicks, the value can be copied and downloaded in one
 * click each, and closing needs an explicit acknowledgement that it has been
 * saved.
 */
export function OneTimeSecret({ value, lines, title, intro, filename, onAcknowledge }: Props) {
  const t = useT();
  const [acknowledged, setAcknowledged] = useState(false);

  // A second secret in the same session (regenerated recovery codes) must not
  // inherit the previous acknowledgement.
  useEffect(() => { if (value) setAcknowledged(false); }, [value]);

  if (!value) return null;

  return (
    <Modal
      opened
      onClose={() => { /* Dismissal runs through the acknowledgement below. */ }}
      title={title}
      size="md"
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
    >
      <Stack gap="md">
        <Text role="bodySmall">{intro}</Text>

        <Alert color="warning">{t('settings.secret.onlyChance')}</Alert>

        <div className="rr-one-time-secret">
          {lines
            ? lines.map((line) => <span key={line}>{line}</span>)
            : <span>{value}</span>}
        </div>

        <Group gap="sm" wrap="wrap">
          <CopyButton value={value}>
            {({ copied, failed, copy }) => (
              <Button size="xs" onClick={copy}>
                {failed ? t('settings.secret.copyFailed') : copied ? t('common.copied') : t('action.copy')}
              </Button>
            )}
          </CopyButton>
          <Button size="xs" variant="default" onClick={() => downloadText(value, filename, 'text/plain')}>
            {t('settings.secret.download')}
          </Button>
        </Group>

        <Checkbox
          label={t('settings.secret.acknowledge')}
          checked={acknowledged}
          onChange={(next) => setAcknowledged(next)}
        />

        <Group justify="flex-end">
          <Button disabled={!acknowledged} onClick={onAcknowledge}>{t('settings.secret.done')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
